import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSearchParams } from 'react-router-dom';
import {
  Text,
  TextProps,
  chakra,
  ChakraComponent,
  Flex,
  Skeleton,
  Switch,
} from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFutbol } from '@fortawesome/pro-light-svg-icons';
import { FEATURE_FLAGS } from '@/constants/featureFlags';
import { useQueryOfferings } from '@/api/offerings/offerings.hooks';
import toggle from '@/assets/pyramidbet/images/toggle/pyramid.png';
import IconSvg from '@/components/common/IconSvg';

import { getIconBySportName } from '@/helpers/utils';
import { ERaceType } from '../../../../lib/DBModels';

const FlexWrapper = chakra(Flex, {
  label: 'desktopToggle-FlexWrapper',
  baseStyle: () => ({
    borderRadius: 'lg',
    alignItems: 'center',
    justifyContent: 'center',
    p: '2',
    mx: ['1.5', null, 'unset'],
    mb: 4,
    gap: '2',
    boxShadow: [
      null,
      null,
      '0px 0px 0px 2px rgba(252, 211, 96, 1) inset, 0px 0px 0px 4px rgba(10, 15, 19, 1) inset, 0px 0px 0px 5px #4DB0C2 inset, 0px 7px 20px 0px rgba(0, 0, 0, 0.3)',
    ],
    bgGradient: 'linear(to-b, beta.400, beta.500)',
    h: '16',
    pos: 'relative',
  }),
});

const TextToggle = chakra(Text, {
  label: 'desktopToggle-TextToggle',
  shouldForwardProp: (prop) => !['isActive'].includes(prop),
  baseStyle: ({ isActive }: any) => ({
    fontFamily: 'accent',
    fontWeight: 'extrabold',
    fontSize: 'sm',
    textTransform: 'uppercase',
    textShadow: isActive
      ? '0px 0px 8px rgba(254, 196, 77, 0.5)'
      : '0px 1px 0px rgba(255, 255, 255, 0.08)',
    color: isActive ? '#FEC44D' : 'blackAlpha.600',
    px: '1.5',

    svg: {
      filter: isActive ? 'drop-shadow(0px 0px 8px rgba(254, 196, 77, 0.5))' : 'drop-shadow(0px 1px 0px rgba(255, 255, 255, 0.08))',
      color: isActive ? '#FEC44D' : 'blackAlpha.600',
    },
  }),
}) as ChakraComponent<'text', TextProps & { isActive: boolean }>;

const Switcher = chakra(Switch, {
  label: 'desktopToggle-Switcher',
  baseStyle: () => ({
    '--switch-track-diff':
      'calc(var(--switch-track-width) - var(--switch-track-height))',
    '--switch-track-width': '78px',
    '--switch-track-height': '40px',
    bg: 'transparent',
    pos: 'relative',
    '.chakra-switch__thumb': {
      bg: 'transparent',
      pos: 'relative',
      transitionDuration: '0.7s',
      zIndex: 'dock',
      _before: {
        bgImg: `url(${toggle})`,
        boxSize: '10',
        bgPos: 'center',
        bgRepeat: 'no-repeat',
        bgSize: 'contain',
        content: '""',
        pos: 'absolute',
        left: '0',
        right: '0',
      },
    },
    '.chakra-switch__track': {
      boxShadow:
        '0px 2px 6px 0px rgba(0, 0, 0, 0.5) inset, 0px 0px 0px 1px #4195A7',
      bg: 'blackAlpha.500',
      borderRadius: '9px',

      _before: {
        bg: 'blackAlpha.600',
        h: '8px',
        w: '52px',
        bgPos: 'center',
        bgRepeat: 'no-repeat',
        bgSize: 'contain',
        borderRadius: '2em',
        boxShadow: '0px 1px 0px 0px rgba(255, 255, 255, 0.09)',
        content: '""',
        pos: 'absolute',
        left: '3.5',
        top: '18px',
      },
    },
  }),
});

const ToggleItemsWrapper = chakra(Flex, {
  label: 'desktopToggle-ToggleItemsWrapper',
  baseStyle: () => ({
    flexDirection: 'column',
    alignItems: 'center',
    fontSize: 'xs',

    svg: {
      boxSize: '19px',
    },
  }),
});

export default function Toggle() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { category } = {
    category: searchParams.get('category'),
  };
  const { data, isInitialLoading } = useQueryOfferings();
  const hasSports = FEATURE_FLAGS.HAS_SPORTS_VIEW;

  if (hasSports && isInitialLoading) {
    return <Skeleton sx={{ h: '44px', mb: '2.5' }} />;
  }

  if (hasSports && !data?.offered_sports?.length) {
    return <></>;
  }

  if (
    !FEATURE_FLAGS.HAS_RACE_VIEW ||
    !hasSports ||
    (hasSports && !data?.offered_sports?.length)
  )
    return <></>;

  return (
    <FlexWrapper>
      <TextToggle
        data-cy="racing-toggle-text"
        isActive={!category || category === 'racing'}
      >
        <ToggleItemsWrapper>
          <IconSvg name={getIconBySportName(ERaceType.Horse)} />
          <FormattedMessage id="racing" defaultMessage="Racing" />
        </ToggleItemsWrapper>
      </TextToggle>

      <Switcher
        onChange={() => {
          setSearchParams({
            category: !category || category === 'racing' ? 'sport' : 'racing',
          });
        }}
        isChecked={category && category !== 'racing'}
        colorScheme="blackAlpha"
        size="lg"
        data-cy="categorySwitcher"
      />
      <TextToggle data-cy="sports-toggle-text" isActive={category === 'sport'}>
        <ToggleItemsWrapper>
          <FontAwesomeIcon icon={faFutbol} />
          <FormattedMessage id="sport" defaultMessage="Sports" />
        </ToggleItemsWrapper>
      </TextToggle>
    </FlexWrapper>
  );
}
